import React, { useState, useEffect, useMemo, useReducer } from "react";
import { makeStyles, Theme } from "@material-ui/core";
import RVModelSelection from "./RVModelSelection";
import { RVModel } from "../../../api/connx/rvmodel";
import gql from "graphql-tag";
import { useQuery } from "@apollo/react-hooks";
import { ApolloClient } from "@apollo/client";
import { ConnXClient } from "../../../api/apolloClient";
import { Loading, ErrorView } from "../../../components";
import Live from "../dcx/Live";
import { useLiveDatas, useOnlineStatuses } from "../dcx/useLiveDatas";
import OnlineStatusFilter, {
  matchesStatusFilter,
  StatusFilter,
} from "../dcx/OnlineStatusFilter";
import { OnDCXDashEvent } from "../dcx/live/ActionMenu";
import { useHistory } from "react-router-dom";
import { useLocationState } from "../../../helpers/useLocationState";
import { OrderProvider } from "../../../helpers/orderContext";

export const ORDERS_QUERY = gql`
  query listRVModelOrders($groupID: ID!) {
    listRVModelOrders(groupID: $groupID) {
      models {
        docID
        name
        iotThingName
        hidden
      }
    }
  }
`;

interface Order {
  docID: string;
  name: string;
  iotThingName: string;
  // hidden by a model/order admin, e.g. a device that no longer reports
  hidden?: boolean;
}

interface QueryResult {
  listRVModelOrders: {
    models: Order[];
  };
}

interface LocationState {
  model: string;
  modelName: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  // first on the page, it is pulled into the page's top padding
  modelSelection: {
    marginTop: theme.spacing(-2),
  },
  dcxLive: {
    padding: theme.spacing(0, 0, 3),
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: theme.spacing(2),
  },
}));

const Dashboard: React.FC<{ client?: ApolloClient<unknown> }> = ({
  client = ConnXClient,
}) => {
  const [rvModel, setModel] = useState<RVModel | undefined>(undefined);

  const history = useHistory<LocationState>();
  const [locationState, setLocationState] = useLocationState<LocationState>();

  // all orders, or only those online or offline
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const handleModelChange = (m?: RVModel): void => {
    setModel(m);
    setStatusFilter("all");

    // Store the currently selected model in the current history location state.
    const newLocationState: LocationState = {
      model: "",
      modelName: "null",
    };
    if (m) {
      newLocationState.model = m.docID;
      newLocationState.modelName = m.name;
    }
    setLocationState(newLocationState);
  };

  const { loading, data, error } = useQuery<QueryResult>(ORDERS_QUERY, {
    skip: rvModel === undefined,
    variables: {
      groupID: rvModel?.docID,
    },
    client: client,
  });

  // Use the location to set the selected model.
  if (locationState) {
    if (locationState.model !== rvModel?.docID) {
      setModel({
        groupID: "",
        docID: locationState.model,
        name: locationState.modelName,
        desc: "",
        revision: -1,
      });
    }
  } else {
    if (undefined !== rvModel) {
      setModel(undefined);
    }
  }

  const [orders, setOrders] = useReducer(
    (
      _prev: Order[] | undefined,
      data: QueryResult | undefined
    ): Order[] | undefined => {
      if (data) {
        // only orders with a device, and not hidden
        return data.listRVModelOrders.models
          .filter((x) => x.iotThingName !== "" && !x.hidden)
          .sort((a, b) => a.name.localeCompare(b.name));
      }
      return undefined;
    },
    undefined
  );

  useEffect(() => {
    setOrders(data);
  }, [data]);

  // the live data of every order of the model, read in one call
  const orderIDs = useMemo(() => (orders || []).map((o) => o.docID), [orders]);
  const liveDatas = useLiveDatas(orderIDs, client);
  const liveData = liveDatas.byOrderID;
  const { statusOf, online, offline } = useOnlineStatuses(orderIDs, liveDatas);

  const handleDCXDash = (name: string): ((e: OnDCXDashEvent) => void) => {
    return (e: OnDCXDashEvent): void => {
      history.push(`/dcx/${e.orderID}/dashboard?dash_name=${name}`);
    };
  };

  const classes = useStyles();

  return (
    <>
      {/* select RV model */}
      <div className={classes.modelSelection}>
        <RVModelSelection
          modelOption={{
            value: "testvalue",
            label: rvModel ? rvModel.name : "",
            data: rvModel,
          }}
          onChange={handleModelChange}
        />
      </div>

      {/* loading data */}
      {loading && <Loading />}

      {/* error */}
      {rvModel && error && <ErrorView />}

      {/* list of 'live' data */}
      {orders && orders.length > 0 && (
        <OnlineStatusFilter
          value={statusFilter}
          onChange={setStatusFilter}
          online={online}
          offline={offline}
        />
      )}
      {orders &&
        "all" !== statusFilter &&
        !orders.some((x) =>
          matchesStatusFilter(statusFilter, statusOf(x.docID))
        ) && (
          <div data-testid="dashboard-no-status-match">
            No {statusFilter} orders.
          </div>
        )}
      {orders && (
        <div className={classes.dcxLive}>
          {orders
            .filter((x) => matchesStatusFilter(statusFilter, statusOf(x.docID)))
            .map((x) => (
              /* FIXME: https://reactjs.org/docs/context.html#caveats */
              <OrderProvider
                key={x.docID}
                docID={x.docID}
                isOwner={false}
                hasThing={"" !== x.iotThingName}
              >
                <Live
                  title={x.name}
                  data={liveData.get(x.docID)}
                  status={statusOf(x.docID)}
                  onDCXDash={handleDCXDash(x.name)}
                />
              </OrderProvider>
            ))}
        </div>
      )}
    </>
  );
};

export default Dashboard;
